import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Globe, Shield, Terminal, Zap, ChevronRight, Activity, Cpu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import bgImage from '../assets/images/bg.png';
import useAutoRefresh from '../hooks/useAutoRefresh';
import {
  CHALLENGES_RELEASE_AT_ISO,
  getChallengesReleaseDate,
  getTimeUntilChallengesUnlock,
  hasChallengesUnlocked
} from '../utils/constants';

const Landing = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ users: 0, teams: 0, challenges: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const [nowMs, setNowMs] = useState(Date.now());
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const challengesUnlocked = hasChallengesUnlocked(nowMs) || isAdmin;
  const countdown = getTimeUntilChallengesUnlock(nowMs);
  const releaseDateText = getChallengesReleaseDate().toLocaleString('en-IN', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'Asia/Kolkata'
  });
  const registrationDeadline = new Date('2026-05-01T17:59:00+05:30');
  const registrationDiffMs = registrationDeadline.getTime() - nowMs;
  const registrationTotalSeconds = Math.max(0, Math.floor(registrationDiffMs / 1000));
  const registrationCountdown = {
    days: Math.floor(registrationTotalSeconds / 86400),
    hours: Math.floor((registrationTotalSeconds % 86400) / 3600),
    minutes: Math.floor((registrationTotalSeconds % 3600) / 60),
    seconds: registrationTotalSeconds % 60,
    hasClosed: registrationDiffMs <= 0
  };
  const registrationDeadlineText = registrationDeadline.toLocaleString('en-IN', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'Asia/Kolkata'
  });

  const fetchStats = async () => {
    try {
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useAutoRefresh(fetchStats, { intervalMs: 30000 });

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case 'web': return <Globe className="text-cyber-blue h-8 w-8" />;
      case 'crypto': return <Shield className="text-cyber-accent h-8 w-8" />;
      case 'forensic': return <Terminal className="text-cyber-green h-8 w-8" />;
      case 'binary': return <Cpu className="text-red-500 h-8 w-8" />;
      case 'reverse engineering': return <Cpu className="text-orange-500 h-8 w-8" />;
      default: return <Zap className="text-yellow-500 h-8 w-8" />;
    }
  };

  const getCategoryDescription = (category) => {
    switch (category.toLowerCase()) {
      case 'web': return 'Test real-world web flaws like SQL injection, XSS, and auth bypass.';
      case 'crypto': return 'Decode ciphers, break encodings, and recover hidden messages.';
      case 'forensic': return 'Investigate files, metadata, and traces to uncover evidence.';
      case 'binary': return 'Reverse binaries, inspect program logic, and find hidden checks.';
      case 'osint': return 'Track public clues across platforms and connect digital footprints.';
      case 'misc': return 'Solve creative logic puzzles and hybrid security mini-challenges.';
      case 'reverse engineering': return 'Analyze APKs, scripts, and binaries to reconstruct original logic and keys.';
      default: return 'Hands-on security tasks designed for practical CTF learning.';
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-cyber-dark text-white font-sans" style={{ backgroundImage: `linear-gradient(rgba(10, 20, 20, 0.8), rgba(10, 20, 20, 0.8)), url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      
      {/* Navigation */}
      <nav className="relative z-10 px-6 py-4 flex justify-between items-center bg-black/20 backdrop-blur-md border-b border-white/5">
        <div className="flex items-center space-x-2">
          <Trophy className="text-cyber-green h-8 w-8" />
          <span className="text-2xl font-black italic tracking-tighter uppercase font-bytebounce">
            <span className="tracking-[0.28em]">CUHP</span>{' '}<span className="text-cyber-green tracking-[0.28em]">CTF</span>
          </span>
        </div>
        <div className="flex items-center space-x-6">
          {user ? (
            <Link to="/dashboard" className="cyber-button text-xs py-2">
              Go to Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-xs font-black uppercase tracking-widest text-white/70 hover:text-cyber-green transition-colors">
                Login
              </Link>
              <Link to="/register" className="cyber-button text-xs py-2 px-6">
                Register
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <main className="relative z-10 pt-20 pb-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-1 rounded-full bg-cyber-green/5 border border-cyber-green/20 text-cyber-green text-[10px] font-black uppercase tracking-[0.2em] mb-6 animate-pulse">
            <Activity className="h-3 w-3 mr-2" /> Live Connection: Established
          </div>
          <h1 className="font-bytebounce font-black uppercase italic leading-[0.95] mb-6 text-3xl sm:text-5xl md:text-7xl lg:text-8xl">
            <span className="block whitespace-nowrap tracking-[0.08em] sm:tracking-[0.16em] md:tracking-[0.2em]">
              Defend. Exploit. <span className="text-cyber-green">Learn.</span>
            </span>
            <span className="block mt-3 whitespace-nowrap tracking-[0.12em] sm:tracking-[0.22em] md:tracking-[0.26em] text-2xl sm:text-4xl md:text-6xl lg:text-7xl">
              In Real Time
            </span>
          </h1>
          <p className="max-w-2xl mx-auto text-white/60 font-mono text-sm md:text-base mb-12 uppercase tracking-wide leading-relaxed">
            Train like a security analyst with practical CTF missions across web,
            crypto, binary, forensics, and osint. Build skills, solve challenges,
            and climb the live leaderboard with your team.
          </p>
          <p className="max-w-2xl mx-auto text-cyber-green/90 font-mono text-xs md:text-sm mb-8 uppercase tracking-wider">
            Event Date: <span className="font-black text-cyber-green">{releaseDateText}</span>
          </p>
          {!user && (
            <div className="max-w-3xl mx-auto mb-10 cyber-glass border border-yellow-400/30 rounded-xl p-6">
              <p className="text-yellow-400 text-[10px] font-black uppercase tracking-[0.25em] mb-3">
                Registration Countdown
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <CountdownBox label="Days" value={registrationCountdown.days} />
                <CountdownBox label="Hours" value={registrationCountdown.hours} />
                <CountdownBox label="Minutes" value={registrationCountdown.minutes} />
                <CountdownBox label="Seconds" value={registrationCountdown.seconds} />
              </div>
              <p className="text-white/70 font-mono text-xs uppercase tracking-wide">
                {registrationCountdown.hasClosed ? 'Registration closed on ' : 'Registration closes on '}
                <span className="text-yellow-300 font-black">{registrationDeadlineText}</span>.
              </p>
            </div>
          )}
          {!challengesUnlocked && user && (
            <div className="max-w-3xl mx-auto mb-10 cyber-glass border border-cyber-blue/30 rounded-xl p-6">
              <p className="text-cyber-blue text-[10px] font-black uppercase tracking-[0.25em] mb-3">
                Challenge Unlock Countdown
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <CountdownBox label="Days" value={countdown.days} />
                <CountdownBox label="Hours" value={countdown.hours} />
                <CountdownBox label="Minutes" value={countdown.minutes} />
                <CountdownBox label="Seconds" value={countdown.seconds} />
              </div>
              <p className="text-white/60 font-mono text-xs uppercase tracking-wide">
                Challenges unlock on <span className="text-cyber-green font-black">{releaseDateText}</span>.
                Registration and team creation are open now.
              </p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Link to={user ? "/dashboard" : "/login"} className="cyber-button px-10 py-4 text-sm w-full sm:w-72 flex items-center justify-center">
              {user ? "Enter Dashboard" : "Get Started"} <ChevronRight className="h-4 w-4 ml-2" />
            </Link>
            {!challengesUnlocked && user ? (
              <div className="cyber-button border-cyber-blue/40 text-cyber-blue/90 px-10 py-4 text-sm w-full sm:w-72 cursor-default">
                <span className="relative z-10 flex items-center justify-center font-black uppercase tracking-wider">
                  Registered Teams: {loading ? '...' : stats.teams}
                </span>
              </div>
            ) : user ? (
              <Link to="/leaderboard" className="cyber-button border-cyber-blue text-cyber-blue hover:bg-cyber-blue hover:text-black px-10 py-4 text-sm w-full sm:w-72 overflow-hidden group">
                <span className="relative z-10 flex items-center justify-center">
                  System Ranking <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ) : null}
            {user && (
              <Link
                to="/challenges"
                className={`cyber-button px-10 py-4 text-sm w-full sm:w-72 overflow-hidden group ${challengesUnlocked ? '' : 'border-white/10 text-white/40 pointer-events-none opacity-50'}`}
                aria-disabled={!challengesUnlocked}
                title={challengesUnlocked ? 'Open challenges' : `Challenges unlock at ${CHALLENGES_RELEASE_AT_ISO}`}
              >
                <span className="relative z-10 flex items-center justify-center">
                  Challenges {challengesUnlocked ? <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" /> : '(Locked)'}
                </span>
              </Link>
            )}
          </div>

          <div className="max-w-4xl mx-auto mt-10 cyber-glass border border-cyber-green/30 rounded-xl p-6 text-left">
            <p className="text-cyber-green text-[10px] font-black uppercase tracking-[0.25em] mb-3">
              Awards And Certificates
            </p>
            <div className="space-y-2 text-white/70 font-mono text-xs uppercase tracking-wide leading-relaxed">
              <p>1st Winner: Memento + Certificate</p>
              <p>2nd Winner: Memento + Certificate</p>
              <p>3rd Winner: Memento + Certificate</p>
              <p>Best Individual Scorer: Memento + Certificate</p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mt-10 cyber-glass border border-red-500/30 rounded-xl p-6 text-left animate-in fade-in zoom-in duration-500">
            <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.25em] mb-4 flex items-center">
              <span className="text-sm mr-2">📜</span> CTF Participation Rules
            </p>
            <div className="space-y-3 text-white/70 font-mono text-[11px] sm:text-xs uppercase tracking-wide leading-relaxed">
              <p><span className="text-red-500 font-bold mr-2">1.</span> Flag sharing is not allowed between teams.</p>
              <p><span className="text-red-500 font-bold mr-2">2.</span> Do not leave the venue without permission.</p>
              <p><span className="text-red-500 font-bold mr-2">3.</span> Flag format: <code className="bg-black/40 px-2 py-1 rounded text-cyber-green font-bold border border-cyber-green/20">CUHP{'{...........}'}</code></p>
              <p><span className="text-red-500 font-bold mr-2">4.</span> Attack only authorized targets. Do not attack other systems/sites.</p>
              <p><span className="text-red-500 font-bold mr-2">5.</span> No submissions will be allowed after the deadline (5:15PM).</p>
              <p><span className="text-red-500 font-bold mr-2">6.</span> Competition duration: 7 hours 15 minutes only.</p>
              <p><span className="text-red-500 font-bold mr-2">7.</span> Do not discuss challenges or solutions with other teams.</p>
              
              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-cyber-blue font-bold text-[10px] sm:text-xs tracking-[0.1em]">
                  For any queries, contact the organizing team.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-24 transition-opacity duration-1000 ${loading ? 'opacity-0' : 'opacity-100'}`}>
          <StatBox label="Registered Players" value={loading ? '...' : `${stats.users}+`} color="cyber-green" />
          <StatBox label="Active Teams" value={loading ? '...' : stats.teams} color="cyber-blue" />
          <StatBox label="Challenge Pool" value={loading ? '...' : stats.challenges} color="cyber-accent" />
          <StatBox label="Platform Uptime" value="24/7" color="cyber-green" />
        </div>

        {/* Categories Section */}
        <div className="py-20 border-t border-white/5">
          <div className="flex items-center space-x-4 mb-12">
            <div className="h-px bg-cyber-green/30 flex-1"></div>
            <h2 className="text-2xl font-black uppercase italic tracking-tighter text-white/40">Challenge Sectors</h2>
            <div className="h-px bg-cyber-green/30 flex-1"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {stats.categories.length > 0 ? (
              stats.categories.map((cat) => (
                <CategoryCard 
                  key={cat}
                  icon={getCategoryIcon(cat)} 
                  title={cat} 
                  description={getCategoryDescription(cat)}
                />
              ))
            ) : (
              // Fallback if API fails or categories are still loading the description logic
              <>
                <CategoryCard icon={<Globe className="text-cyber-blue h-8 w-8" />} title="Web" description="Find and exploit common vulnerabilities in modern web apps." />
                <CategoryCard icon={<Terminal className="text-cyber-green h-8 w-8" />} title="Forensic" description="Extract hidden evidence from files, logs, and traffic artifacts." />
                <CategoryCard icon={<Shield className="text-cyber-accent h-8 w-8" />} title="Crypto" description="Decode encoded text and solve cryptographic puzzle chains." />
              </>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-12 px-6 border-t border-white/5 bg-black/40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center text-center md:text-left space-y-8 md:space-y-0 text-white/30">
          <div>
            <div className="flex items-center justify-center md:justify-start space-x-2 mb-4">
              <Trophy className="text-cyber-green h-6 w-6" />
              <span className="text-xl font-black uppercase italic font-bytebounce text-white/50">
                <span className="tracking-[0.28em]">CUHP</span>{' '}<span className="tracking-[0.28em]">CTF</span>
              </span>
            </div>
            <p className="text-[10px] font-mono leading-relaxed uppercase">
              Central University of Himachal Pradesh <br />
              Capture The Flag Initiative
            </p>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest">
            Challenges: {challengesUnlocked ? 'Live' : 'Locked'} <br />
            Mode: Team Competition
          </div>
          <div className="text-[10px] font-mono uppercase">
            © {new Date().getFullYear()} <span className="tracking-[0.28em]">CUHP</span>. <br />
            Built for practical cybersecurity learning.
          </div>
        </div>
      </footer>
    </div>
  );
};

const CategoryCard = ({ icon, title, description }) => (
  <div className="cyber-card p-8 group hover:scale-[1.02] transition-all duration-300 relative overflow-hidden">
    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
      {icon}
    </div>
    <div className="mb-6 relative z-10">{icon}</div>
    <h3 className="text-xl font-black uppercase italic mb-3 tracking-tighter group-hover:text-cyber-green transition-colors relative z-10">{title}</h3>
    <p className="text-white/40 text-[11px] font-mono leading-relaxed uppercase relative z-10">{description}</p>
  </div>
);

const StatBox = ({ label, value, color }) => (
  <div className="cyber-glass p-6 text-center rounded-xl border border-white/5">
    <div className={`text-4xl font-black font-bytebounce mb-2 uppercase tracking-tighter text-${color}`}>
      {value}
    </div>
    <div className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{label}</div>
  </div>
);

const CountdownBox = ({ label, value }) => (
  <div className="rounded-lg border border-cyber-blue/20 bg-black/30 px-4 py-3 text-center">
    <p className="text-2xl font-black text-cyber-green font-bytebounce">{String(value).padStart(2, '0')}</p>
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">{label}</p>
  </div>
);

export default Landing;
